import { Badge } from '@pesto/ui/components/Badge';
import { CardHeader } from '@pesto/ui/components/Card';
import { Typography } from '@pesto/ui/components/Typography';

import type { EntityHeaderConfig } from '../types/EntityHeader.types';

export interface EntityHeaderProps {
  config: EntityHeaderConfig;
  className?: string;
}

export const EntityHeader = ({ config, className }: EntityHeaderProps) => {
  const { avatar, title, description, secondary, meta, badges, actions } = config;
  const AvatarIcon = avatar?.icon;
  const showAvatar = AvatarIcon || avatar?.src;

  return (
    <CardHeader className={className ?? 'flex flex-row items-start justify-between gap-4'}>
      <div className="flex items-start gap-4">
        {showAvatar && (
          <div className="bg-muted flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg">
            {avatar?.src ? (
              <img src={avatar.src} alt="" className="h-full w-full object-cover" />
            ) : AvatarIcon ? (
              <AvatarIcon className="text-muted-foreground h-6 w-6" />
            ) : null}
          </div>
        )}
        <div className="space-y-1">
          <Typography variant="headerLarge" className="text-xl font-semibold">
            {title}
          </Typography>
          {description && (
            <Typography variant="body" className="text-muted-foreground text-sm">
              {description}
            </Typography>
          )}
          {secondary && (
            <Typography variant="caption" className={`text-muted-foreground ${secondary.mono ? 'font-mono' : ''}`}>
              {secondary.label ? `${secondary.label} ${secondary.value}` : secondary.value}
            </Typography>
          )}
          {meta && meta.length > 0 && (
            <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 pt-1">
              {meta.map((item, idx) => {
                const ItemIcon = item.icon;

                return (
                  <span key={`${item.value}-${idx}`} className="inline-flex items-center gap-1.5 text-sm">
                    {ItemIcon && <ItemIcon className="h-3.5 w-3.5" />}
                    {item.value}
                  </span>
                );
              })}
            </div>
          )}
          {badges && badges.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              {badges.map((badge) => {
                const BadgeIcon = badge.icon;

                return (
                  <Badge key={badge.label} variant={badge.variant ?? 'outline'} className="text-xs">
                    {BadgeIcon && <BadgeIcon className="mr-1 h-3 w-3" />}
                    {badge.label}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </CardHeader>
  );
};
